import { animationFields } from '../_shared/universal.js';
/**
 * section-divider — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Section divider" section
 * (shrine-pro-ref/sections/section-divider.liquid). A purely decorative SVG shape (17 presets: diagonals,
 * curves, sharps, waves + a random line) laid BETWEEN two colour bands — the shape fills in one colour,
 * the space above it in another — so a page can transition between sections with a wave / slope / zig-zag
 * instead of a hard edge. The section carries NO blocks; it is chrome, not content.
 *
 * Config is faithful to Shrine, one-for-one: `visibility` (device display), `shape` (the 17 presets),
 * `flip_horizontal` / `flip_vertical` (mirror the shape — vertical flip ALSO swaps which band gets which
 * colour, exactly as Shrine's liquid does), `shape_color` + `background_color` (each of the 6 schemes, with
 * a custom colour revealed by `visible_if`), and the standard Section-padding pair (mobile = ×0.75).
 *
 * ONE setting is ours, not Shrine's: `animateWaves` (default ON, shown only for the three wave shapes).
 * Shrine animates `waves_3` alone and forces it; we animate all three and let the seller stop them — so
 * the shape option is labelled plainly "Waves 3", the "(Animated)" tag having become meaningless.
 *
 * Field-type mapping (Shopify → ours): select→select, checkbox→checkbox, color→color, range→range,
 * header→group, visible_if→visibleWhen. Ids are clean camelCase; labels / options / defaults stay faithful
 * to Shrine (incl. the `inverse` value labelled "Text"). The 17 SVGs are captured VERBATIM in shapes.ts and
 * injected as raw HTML; the SVG geometry + base.css divider rules are ported unchanged, only the Shrine
 * `rgb(var(--color-*))` scheme colours are remapped to our `--aqx-theme-*` tokens (the house rule).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const PADDING = 'Section padding';
/** The 6 Shrine colour-scheme choices shared by "Shape color" and "Background color" (Shrine parity). */
const SCHEME_6 = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Text' },
    { value: 'custom', label: 'Custom' },
];
/** The 17 Shrine shape presets, in Shrine's exact order + labels. */
const SHAPE_OPTIONS = [
    { value: 'diagonal_1', label: 'Diagonal 1' },
    { value: 'diagonal_2', label: 'Diagonal 2' },
    { value: 'curved_1', label: 'Curved 1' },
    { value: 'curved_2', label: 'Curved 2' },
    { value: 'curved_3', label: 'Curved 3' },
    { value: 'curved_4', label: 'Curved 4' },
    { value: 'curved_5', label: 'Curved 5' },
    { value: 'curved_6', label: 'Curved 6' },
    { value: 'sharp_1', label: 'Sharp 1' },
    { value: 'sharp_2', label: 'Sharp 2' },
    { value: 'sharp_3', label: 'Sharp 3' },
    { value: 'sharp_4', label: 'Sharp 4' },
    { value: 'sharp_5', label: 'Sharp 5' },
    { value: 'waves_1', label: 'Waves 1' },
    { value: 'waves_2', label: 'Waves 2' },
    { value: 'waves_3', label: 'Waves 3' },
    { value: 'random_1', label: 'Random 1' },
];
export const sectionDividerSchema = {
    type: 'section-divider',
    name: 'Section divider',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        {
            id: 'shape',
            type: 'select',
            label: 'Shape',
            default: 'diagonal_1',
            options: SHAPE_OPTIONS,
        },
        {
            id: 'flipHorizontal',
            type: 'checkbox',
            label: 'Flip horizontal',
            default: false,
            info: 'Mirror the shape left-to-right.',
        },
        {
            id: 'flipVertical',
            type: 'checkbox',
            label: 'Flip vertical (point down)',
            default: false,
            info: 'Turns the shape upside-down so it points DOWNWARD. Leave off for a divider at the TOP of a section; turn on for one at the BOTTOM.',
        },
        {
            id: 'animateWaves',
            type: 'checkbox',
            label: 'Animate the waves',
            default: true,
            info: 'The waves drift sideways and swell, instead of standing still. Only affects the three wave shapes.',
            visibleWhen: { field: 'shape', equals: ['waves_1', 'waves_2', 'waves_3'] },
        },
        {
            id: 'shapeColor',
            type: 'select',
            label: 'Shape color',
            default: 'accent-1',
            options: SCHEME_6,
        },
        {
            id: 'customShapeColor',
            type: 'color',
            label: 'Custom shape color',
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'shapeColor', equals: 'custom' },
        },
        {
            id: 'backgroundColor',
            type: 'select',
            label: 'Background color',
            default: 'background-1',
            options: SCHEME_6,
        },
        {
            id: 'customBackgroundColor',
            type: 'color',
            label: 'Custom background color',
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'backgroundColor', equals: 'custom' },
        },
        // ── Section padding (Shrine default 0 / mobile = ×0.75) ───────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 0,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 0,
        },
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    maxBlocks: 0,
};
export const sectionDividerPresets = [
    {
        name: 'Section divider',
        settings: {
            visibility: 'always-display',
            shape: 'diagonal_1',
            flipHorizontal: false,
            flipVertical: false,
            animateWaves: true,
            shapeColor: 'accent-1',
            backgroundColor: 'background-1',
            paddingTop: 0,
            paddingBottom: 0,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const sectionDividerMeta = {
    label: 'Section divider',
    group: 'Layout',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map